import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { useLang } from "@/context/LanguageContext";
import { Phone, Mail, MapPin, Clock, Send, User, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_HERO = "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/7dc77085bd950e780d35d5ac750f49fe919d743e47f4fcefd887e07f4e5cb099.jpeg";

const PLOT_SIZES = [
  "500 Sq.Mtr", "1000 Sq.Mtr", "2000 Sq.Mtr", "3000 Sq.Mtr",
  "5000 Sq.Mtr", "10000 Sq.Mtr", "Custom Size"
];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", plot_size: "", message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry submitted successfully! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", plot_size: "", message: "" });
    } catch {
      toast.error("Failed to submit. Please call +91-9258959058");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="contact" />
      <Navbar />
      <PageHero
        image={CONTACT_HERO}
        tag="Get In Touch"
        title={t("contact.title")}
        subtitle={t("contact.subtitle")}
        breadcrumb={{ home: t("nav.home"), current: t("contact.breadcrumb") }}
      />

      <div className="bg-[#0F172A] py-12" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-5">
              <div className="bg-[#1E293B] border border-white/10 text-white p-6">
                <h3
                  className="font-bold text-xl mb-4 uppercase"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.05em" }}
                >
                  {t("contact.officeDetails")}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-sm mb-0.5">{t("contact.officeAddress")}</div>
                      <div className="text-slate-300 text-sm leading-relaxed">
                        Near IIE Sigaddi, SIIDCUL,<br />
                        Kotdwar, Pauri Garhwal,<br />
                        Uttarakhand — 246149
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-sm mb-0.5">{t("contact.phoneNumbers")}</div>
                      <a href="tel:+919258959058" className="block text-slate-300 text-sm hover:text-orange-400 transition-colors">
                        +91-9258959058
                      </a>
                      <a href="tel:+919557877290" className="block text-slate-300 text-sm hover:text-orange-400 transition-colors">
                        +91-9557877290
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-sm mb-0.5">{t("contact.emailAddress")}</div>
                      <a href="mailto:amritinfotech9@gmail.com" className="text-slate-300 text-sm hover:text-orange-400 transition-colors">
                        amritinfotech9@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-sm mb-0.5">{t("contact.officeHours")}</div>
                      <div className="text-slate-300 text-sm">{t("contact.monSat")}</div>
                      <div className="text-slate-400 text-xs">{t("contact.sunday")}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Info */}
              <div className="bg-[#1E293B] border border-white/10 p-5">
                <h4 className="font-bold text-white mb-3 text-sm uppercase tracking-wide">
                  {t("contact.plotPriceSummary")}
                </h4>
                <table className="w-full text-sm">
                  <tbody>
                    {[
                      ["500 Sq.Mtr", "\u20B97,500/sqmtr"],
                      ["1,000 Sq.Mtr", "\u20B97,500/sqmtr"],
                      ["2,000 Sq.Mtr", "\u20B97,500/sqmtr"],
                      ["5,000 Sq.Mtr", "\u20B97,500/sqmtr"],
                      ["10,000 Sq.Mtr", "\u20B97,500/sqmtr"],
                    ].map(([size, price]) => (
                      <tr key={size} className="border-b border-white/[0.06] last:border-0">
                        <td className="py-2 text-slate-400">{size}</td>
                        <td
                          className="py-2 text-right font-bold text-orange-400"
                          style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.8rem" }}
                        >
                          {price}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 bg-[#1E293B] border border-white/10 p-6 md:p-8">
              <h3
                className="text-white font-bold text-2xl mb-1 uppercase"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {t("contact.sendEnquiry")}
              </h3>
              <p className="text-slate-400 text-sm mb-6">
                {t("contact.formSubtitle")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      <User className="inline w-3.5 h-3.5 mr-1" /> {t("contact.fullName")} *
                    </label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      <Phone className="inline w-3.5 h-3.5 mr-1" /> {t("contact.phone")} *
                    </label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <Mail className="inline w-3.5 h-3.5 mr-1" /> {t("contact.email")}
                  </label>
                  <input
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    type="email"
                    className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    {t("contact.plotSizeInterest")}
                  </label>
                  <select
                    name="plot_size"
                    value={form.plot_size}
                    onChange={handleChange}
                    className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white"
                    data-testid="contact-size-select"
                  >
                    <option value="">{t("contact.selectPlotSize")}</option>
                    {PLOT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <MessageSquare className="inline w-3.5 h-3.5 mr-1" /> {t("contact.message")} *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Describe your requirements, preferred plot size, industry type, etc."
                    required
                    rows={5}
                    className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? t("contact.submitting") : t("contact.submitEnquiry")}
                </button>

                <p className="text-center text-xs text-slate-500">
                  {t("contact.disclaimer")}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      {/* Virtual Site Tour */}
      <div className="bg-[#1E293B] border-t border-white/[0.06]" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-center mb-6">
            <span className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Virtual Site Tour</span>
            <div className="section-divider mt-2"></div>
            <h3
              className="text-white font-bold text-xl uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {t("contact.ourLocation")}
            </h3>
            <p className="text-slate-400 text-sm mt-1">Explore IIE Sigaddi, SIIDCUL Kotdwar on Google Maps — Satellite & Street View</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-white/10 overflow-hidden" style={{ height: "380px" }}>
              <iframe
                title="IIE Sigaddi Location - Map View"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13796.82!2d78.5150!3d29.7500!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390a1438fce74fd5%3A0x6b65c7ca8f5a35e7!2sKotdwar%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="border border-white/10 overflow-hidden" style={{ height: "380px" }}>
              <iframe
                title="IIE Sigaddi - Satellite View"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6898.41!2d78.5150!3d29.7500!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390a1438fce74fd5%3A0x6b65c7ca8f5a35e7!2sKotdwar%2C%20Uttarakhand!5e1!3m2!1sen!2sin!4v1700000000001!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
          <p className="text-center text-xs text-slate-500 mt-3">
            Click on the maps to zoom, pan, and explore the area around IIE Sigaddi, SIIDCUL Kotdwar
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
